import {
  action,
  atom,
  onUpdate,
  reatomAsync,
  withAbort,
} from "@reatom/framework";
import {
  ProductControllerSearchSortByEnum,
  ProductResponseV2Dto,
  ProductSourceDtoSourceEnum,
} from "definitions/product-storage";
import { API } from "src/api";
import { withDebounce, withIsLoadingAtom } from "src/common/pipes";
import { productSelectionStore } from "src/store/atoms";

import { PriceRange } from "../../components/ProductsTable/components";
import { filterAtom, sortAtom } from "../atoms";
import {
  cursorHistoryAtom,
  currentPerPageAtom,
  resetCursorHistory,
  cursorHistoryLengthAtom,
  nextCursorAtom,
  addCursorToHistory,
} from "../atoms/cursorPagination";

const MAX_PRODUCTS_COUNT = 400;

export interface ProductTableData {
  id: number;
  logoUrl: string;
  name: string;
  article: string;
  priceRange: PriceRange;
  sources: ProductSourceDtoSourceEnum[];
}

export const productsAtom = atom<ProductTableData[]>([]);

const hasSearchError = atom(false);

export const searchProductFetcher = reatomAsync(
  async (ctx, cursor: string | undefined) => {
    const hasError = ctx.get(hasSearchError);

    if (hasError) {
      hasSearchError(ctx, false);
      return null;
    }

    const filterData = ctx.get(filterAtom);
    const sortData = ctx.get(sortAtom);

    const selectedProductSample = ctx.get(
      productSelectionStore.currentSelectionAtom,
    );

    try {
      const response = await API.productStorageApi
        .productControllerSearchV2(
          {
            query: filterData.search || undefined,
            categories: filterData.categoryIds?.map(Number),
            brands: filterData.brandIds?.map(Number),
            priceMin: filterData.priceFrom,
            priceMax: filterData.priceTo,
            currency: filterData.currency ?? "RUB",
            cursor,
            genders: filterData.genders,
            sources: filterData.sources,
            includeCustom: filterData.includeCustom,
            limit: ctx.get(currentPerPageAtom),
            sortBy:
              sortData?.type ?? ProductControllerSearchSortByEnum.Popularity,
            ...(filterData.viewAllSelected && {
              selectionId: selectedProductSample?.id,
            }),
            excludeSelectionIds: filterData.excludeSamples?.map(Number),
          },
          {
            signal: ctx.controller.signal,
          },
        )
        .then((res) => res.data);

      return response;
    } catch (error) {
      hasSearchError(ctx, true);
      console.error("Error fetching search products:", error);
      throw error;
    }
  },
  "fetchSearchProductFetcher",
).pipe(withAbort(), withIsLoadingAtom());

const hasNext = atom(false);

const loadPage = action(async (ctx, cursor: string | undefined) => {
  const searchResult = await ctx.schedule(() =>
    searchProductFetcher(ctx, cursor),
  );
  const meta = searchResult?.meta;

  hasNext(ctx, meta?.hasNext ?? false);
  nextCursorAtom(ctx, meta?.cursor);

  return searchResult;
}, "loadPage");

const isRefreshing = atom(false, "isRefreshing");

const formatProductList = (
  product: ProductResponseV2Dto,
): ProductTableData => ({
  id: product.id || 0,
  logoUrl: product.logoUrl || "",
  name: product.title || "",
  article: product.article || "",
  priceRange: {
    minPrice: product?.minPrice,
    maxPrice: product?.maxPrice,
    currency: product?.currency || "RUB",
  },
  sources: product.sources || [],
});

export const addProducts = action(async (ctx) => {
  const nextCursor = ctx.get(nextCursorAtom);

  if (!nextCursor) {
    return;
  }

  if (nextCursor) {
    addCursorToHistory(ctx, nextCursor);
  }

  const currentProducts = ctx.get(productsAtom);
  const newProducts = await ctx.schedule(() => loadPage(ctx, nextCursor));
  const productList = newProducts?.data;

  if (!productList) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  let updatedProducts = [...currentProducts, ...newProductsMapped];

  if (updatedProducts.length >= MAX_PRODUCTS_COUNT) {
    const countElementsToRemove = productList.length;
    updatedProducts = updatedProducts.slice(countElementsToRemove);
  }

  productsAtom(ctx, updatedProducts);
}, "addProducts");

export const updateProducts = action(async (ctx) => {
  isRefreshing(ctx, true);
  productsAtom(ctx, []);
  resetCursorHistory(ctx);
  nextCursorAtom(ctx, undefined);

  const newProducts = await ctx.schedule(() => loadPage(ctx, undefined));
  const productList = newProducts?.data;

  if (!productList?.length) {
    isRefreshing(ctx, false);
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  productsAtom(ctx, newProductsMapped);
  isRefreshing(ctx, false);
}, "updateProducts");

const debouncedUpdateProducts = updateProducts.pipe(withDebounce(400));

onUpdate(filterAtom, debouncedUpdateProducts);

const isLoading = atom((ctx) => {
  const isFetch = ctx.spy(searchProductFetcher.isLoadingAtom);
  const products = ctx.spy(productsAtom);

  return isFetch && !products.length;
}, "isLoading");

export const loadNextPage = action(async (ctx) => {
  const nextCursor = ctx.get(nextCursorAtom);

  if (!nextCursor) {
    return;
  }

  addCursorToHistory(ctx, nextCursor);

  const newProducts = await ctx.schedule(() => loadPage(ctx, nextCursor));
  const productList = newProducts?.data;

  if (!productList?.length) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  productsAtom(ctx, newProductsMapped);
}, "loadNextPage");

export const loadPreviousPage = action(async (ctx) => {
  const historyLength = ctx.get(cursorHistoryLengthAtom);

  if (historyLength <= 0) {
    return;
  }

  const updatedHistory = cursorHistoryAtom(ctx, (prev) => prev.slice(0, -1));
  const lastCursor = updatedHistory[updatedHistory.length - 1];

  const newProducts = await ctx.schedule(() => loadPage(ctx, lastCursor));
  const productList = newProducts?.data;

  if (!productList?.length) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  productsAtom(ctx, newProductsMapped);
}, "loadPreviousPage");

export const resetToFirstPage = action(async (ctx) => {
  resetCursorHistory(ctx);
  await updateProducts(ctx);
}, "resetToFirstPage");

export const searchProductStore = {
  productsAtom,
  addProducts,
  updateProducts,
  loadNextPage,
  loadPreviousPage,
  resetToFirstPage,
  isFetchLoading: searchProductFetcher.isLoadingAtom,
  isLoading,
  isPaginationLoading: searchProductFetcher.isLoadingAtom,
  hasNext,
  isRefreshing,
};
